<template>
  <a-drawer width="70%" :visible="open" :body-style="{ paddingBottom: '24px' }" @close="onClose">
    <template slot="title">
      属性模板<span v-if="categoryName"> - {{ categoryName }}</span>
    </template>
    <a-alert
      type="info"
      show-icon
      style="margin-bottom: 12px"
      message="属性名称在此按分类配置，商品发布时只填属性值。子分类会继承父分类属性，同名属性以子分类为准。" />
    <div style="margin-bottom: 12px">
      <a-button type="primary" icon="plus" @click="handleAdd" v-hasPermi="['goods:category:add']">新增属性</a-button>
      <a-button style="margin-left: 8px" icon="copy" @click="openCopy" v-hasPermi="['goods:category:add']">从其他分类复制</a-button>
    </div>
    <a-table
      :loading="loading"
      size="small"
      rowKey="id"
      :columns="columns"
      :data-source="list"
      :pagination="false"
      :bordered="true">
      <span slot="inputType" slot-scope="text">{{ inputTypeName(text) }}</span>
      <span slot="isRequire" slot-scope="text">{{ text ? '是' : '否' }}</span>
      <span slot="isCompare" slot-scope="text">{{ text ? '是' : '否' }}</span>
      <span slot="isAppVisible" slot-scope="text">{{ text ? '是' : '否' }}</span>
      <span slot="operation" slot-scope="text, record">
        <a-tag v-if="record.isInherited" color="orange">继承</a-tag>
        <template v-else>
          <a @click="handleEdit(record)" v-hasPermi="['goods:category:edit']">
            <a-icon type="edit" />修改
          </a>
          <a-divider type="vertical" />
          <a @click="handleDelete(record)" v-hasPermi="['goods:category:remove']">
            <a-icon type="delete" />删除
          </a>
        </template>
      </span>
    </a-table>

    <!-- 新增/修改属性 -->
    <a-modal
      :title="formTitle"
      :visible="modalVisible"
      :confirm-loading="submitLoading"
      width="620px"
      @cancel="modalVisible = false"
      @ok="handleSubmit">
      <a-form-model ref="form" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }" :model="form" :rules="rules">
        <a-form-model-item label="一级分组" prop="groupName">
          <a-auto-complete
            v-model="form.groupName"
            :data-source="groupNameOptions"
            placeholder="如 基础信息 / 左手 / 音源系列 / 键盘系列 / 其他" />
        </a-form-model-item>
        <a-form-model-item label="属性名称" prop="paramName">
          <a-input v-model="form.paramName" :maxLength="64" placeholder="如 定位 / 尺寸 / 重量 / 产地 / 自由低音排簧数" />
        </a-form-model-item>
        <a-form-model-item label="录入类型" prop="inputType">
          <a-radio-group v-model="form.inputType" button-style="solid" @change="onInputTypeChange">
            <a-radio-button v-for="item in inputTypeOptions" :value="item.type" :key="item.type">{{ item.name }}</a-radio-button>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item v-if="needOption" label="候选值" prop="optionText">
          <a-input v-model="form.optionText" type="textarea" :rows="3" placeholder="多个候选值用逗号分隔，如 普通款,进阶款" />
        </a-form-model-item>
        <a-form-model-item label="单位" prop="unit">
          <a-input v-model="form.unit" :maxLength="16" placeholder="如 cm / kg" />
        </a-form-model-item>
        <a-form-model-item label="是否必填" prop="isRequire">
          <a-switch v-model="form.isRequire" checked-children="是" un-checked-children="否" />
        </a-form-model-item>
        <a-form-model-item label="参与对比" prop="isCompare">
          <a-switch v-model="form.isCompare" checked-children="是" un-checked-children="否" />
        </a-form-model-item>
        <a-form-model-item label="APP可见" prop="isAppVisible">
          <a-switch v-model="form.isAppVisible" checked-children="是" un-checked-children="否" />
        </a-form-model-item>
        <a-form-model-item label="一级分组排序" prop="groupSort">
          <a-input-number v-model="form.groupSort" :min="0" style="width: 100%" placeholder="越小越靠前" />
        </a-form-model-item>
        <a-form-model-item label="属性排序" prop="sort">
          <a-input-number v-model="form.sort" :min="0" style="width: 100%" placeholder="越小越靠前" />
        </a-form-model-item>
        <a-form-model-item label="备注" prop="remark">
          <a-input v-model="form.remark" :maxLength="200" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 从其他分类复制 -->
    <a-modal
      title="从其他分类复制属性模板"
      :visible="copyVisible"
      :confirm-loading="copyLoading"
      @cancel="copyVisible = false"
      @ok="handleCopy">
      <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="来源分类">
          <a-select
            v-model="copyForm.fromCategoryId"
            show-search
            :filter-option="filterOption"
            placeholder="请选择来源分类"
            style="width: 100%">
            <a-select-option v-for="item in categoryOptions" :value="item.id" :key="item.id">
              {{ item.categoryName }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="覆盖同名属性">
          <a-switch v-model="copyForm.isCover" checked-children="是" un-checked-children="否" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </a-drawer>
</template>

<script>
import {
  paramTemplateList,
  addParamTemplate,
  updateParamTemplate,
  delParamTemplate,
  copyParamTemplate
} from '@/api/goods/paramTemplate'
import { platformCategoryTree } from '@/api/goods/category'

export default {
  name: 'ParamTemplateDrawer',
  data () {
    return {
      open: false,
      loading: false,
      submitLoading: false,
      copyLoading: false,
      categoryId: null,
      categoryName: '',
      list: [],
      categoryOptions: [],
      groupNameOptions: [],
      inputTypeOptions: [
        { type: 1, name: '文本' },
        { type: 2, name: '数值' },
        { type: 3, name: '单选' },
        { type: 4, name: '多选' }
      ],
      columns: [
        { title: '一级分组', dataIndex: 'groupName', key: 'groupName', width: 140 },
        { title: '二级属性', dataIndex: 'paramName', key: 'paramName', width: 160 },
        { title: '录入类型', dataIndex: 'inputType', key: 'inputType', width: 100, scopedSlots: { customRender: 'inputType' } },
        { title: '单位', dataIndex: 'unit', key: 'unit', width: 80 },
        { title: '必填', dataIndex: 'isRequire', key: 'isRequire', width: 70, scopedSlots: { customRender: 'isRequire' } },
        { title: '对比', dataIndex: 'isCompare', key: 'isCompare', width: 70, scopedSlots: { customRender: 'isCompare' } },
        { title: 'APP可见', dataIndex: 'isAppVisible', key: 'isAppVisible', width: 90, scopedSlots: { customRender: 'isAppVisible' } },
        { title: '排序', dataIndex: 'sort', key: 'sort', width: 70 },
        { title: '操作', dataIndex: 'operation', key: 'operation', width: 140, scopedSlots: { customRender: 'operation' } }
      ],
      modalVisible: false,
      formTitle: '',
      form: this.emptyForm(),
      rules: {
        groupName: [{ required: true, message: '请输入一级分组名称', trigger: 'change' }],
        paramName: [{ required: true, message: '请输入属性名称', trigger: 'blur' }],
        inputType: [{ required: true, message: '请选择录入类型', trigger: 'change' }],
        optionText: [{ validator: this.validateOptionText, trigger: 'blur' }]
      },
      copyVisible: false,
      copyForm: { fromCategoryId: undefined, isCover: false }
    }
  },
  computed: {
    needOption () {
      return this.form.inputType === 3 || this.form.inputType === 4
    }
  },
  methods: {
    emptyForm () {
      return {
        id: null,
        categoryId: null,
        groupName: undefined,
        groupSort: 0,
        paramName: undefined,
        inputType: 1,
        unit: '',
        optionText: '',
        isRequire: false,
        isCompare: true,
        isFilter: false,
        isAppVisible: true,
        sort: 0,
        remark: ''
      }
    },
    openDrawer (category) {
      this.categoryId = category.id
      this.categoryName = category.categoryName
      this.open = true
      this.getList()
    },
    onClose () {
      this.open = false
    },
    getList () {
      this.loading = true
      paramTemplateList({ categoryId: this.categoryId, withInherited: true }).then(res => {
        const rows = []
        const groupNames = []
        const groups = res.data || []
        groups.forEach(group => {
          if (group.groupName && groupNames.indexOf(group.groupName) < 0) {
            groupNames.push(group.groupName)
          }
          (group.paramList || []).forEach(item => rows.push(item))
        })
        this.list = rows
        this.groupNameOptions = groupNames
      }).finally(() => {
        this.loading = false
      })
    },
    inputTypeName (type) {
      const item = this.inputTypeOptions.find(e => e.type === type)
      return item ? item.name : ''
    },
    filterOption (input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    onInputTypeChange () {
      this.$refs.form && this.$refs.form.clearValidate('optionText')
    },
    validateOptionText (rule, value, callback) {
      if (!this.needOption) {
        callback()
        return
      }
      if (!value || !value.trim()) {
        callback(new Error('单选/多选类型必须填写候选值'))
        return
      }
      callback()
    },
    handleAdd () {
      this.form = this.emptyForm()
      this.form.categoryId = this.categoryId
      this.formTitle = '新增属性'
      this.modalVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    handleEdit (record) {
      this.form = this.emptyForm()
      this.form.id = record.id
      this.form.categoryId = record.categoryId
      this.form.groupName = record.groupName
      this.form.groupSort = record.groupSort || 0
      this.form.paramName = record.paramName
      this.form.inputType = record.inputType || 1
      this.form.unit = record.unit || ''
      this.form.optionText = this.parseOptionText(record.optionJson)
      this.form.isRequire = !!record.isRequire
      this.form.isCompare = !!record.isCompare
      this.form.isFilter = !!record.isFilter
      this.form.isAppVisible = record.isAppVisible !== false
      this.form.sort = record.sort || 0
      this.form.remark = record.remark || ''
      this.formTitle = '修改属性'
      this.modalVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    parseOptionText (optionJson) {
      if (!optionJson) {
        return ''
      }
      try {
        const arr = JSON.parse(optionJson)
        return Array.isArray(arr) ? arr.join(',') : ''
      } catch (e) {
        return ''
      }
    },
    buildOptionJson () {
      if (!this.needOption) {
        return ''
      }
      const arr = this.form.optionText.split(/[,，\n]/).map(e => e.trim()).filter(e => e)
      return JSON.stringify(arr)
    },
    handleSubmit () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        this.submitLoading = true
        const data = {
          categoryId: this.form.categoryId,
          groupName: this.form.groupName,
          groupSort: this.form.groupSort,
          paramName: this.form.paramName,
          inputType: this.form.inputType,
          unit: this.form.unit,
          optionJson: this.buildOptionJson(),
          isRequire: this.form.isRequire,
          isCompare: this.form.isCompare,
          isFilter: this.form.isFilter,
          isAppVisible: this.form.isAppVisible,
          sort: this.form.sort,
          remark: this.form.remark
        }
        const request = this.form.id ? updateParamTemplate : addParamTemplate
        if (this.form.id) {
          data.id = this.form.id
        }
        request(data).then(() => {
          this.$message.success(this.form.id ? '修改成功' : '新增成功')
          this.modalVisible = false
          this.getList()
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    handleDelete (record) {
      const that = this
      this.$confirm({
        title: '确认删除',
        content: `确定删除属性「${record.paramName}」吗？`,
        onOk () {
          return delParamTemplate({ idList: [record.id] }).then(() => {
            that.$message.success('删除成功')
            that.getList()
          })
        }
      })
    },
    openCopy () {
      this.copyForm = { fromCategoryId: undefined, isCover: false }
      this.copyVisible = true
      if (this.categoryOptions.length === 0) {
        platformCategoryTree({}).then(res => {
          const rows = []
          const walk = list => {
            (list || []).forEach(item => {
              rows.push(item)
              walk(item.children)
            })
          }
          walk(res.data)
          this.categoryOptions = rows.filter(item => item.id !== this.categoryId)
        })
      }
    },
    handleCopy () {
      if (!this.copyForm.fromCategoryId) {
        this.$message.warning('请选择来源分类')
        return
      }
      this.copyLoading = true
      copyParamTemplate({
        fromCategoryId: this.copyForm.fromCategoryId,
        toCategoryId: this.categoryId,
        isCover: this.copyForm.isCover
      }).then(res => {
        this.$message.success(`已复制 ${res.data} 条属性`)
        this.copyVisible = false
        this.getList()
      }).finally(() => {
        this.copyLoading = false
      })
    }
  }
}
</script>
